<template>
    <div class="diagnostics-container">
        <!-- Header -->
        <b-row class="mb-3 align-items-center">
            <b-col cols="12" md="8">
                <h2 class="mb-0">Diagnósticos</h2>
                <p class="text-muted mb-0">Gestiona tus evaluaciones de seguridad</p>
            </b-col>
            <b-col cols="12" md="4" class="text-md-right">
                <b-button 
                    variant="primary" 
                    @click="openCreateModal"
                    :disabled="!selectedCompanyId">
                    <i class="fa fa-plus"></i> Nuevo Diagnóstico
                </b-button>
            </b-col>
        </b-row>

        <!-- Filtros -->
        <b-card class="mb-3">
            <filters-diagnostics 
                ref="filtersComponent"
                :loading="loading"
                :show-filters="showFilters"
                :selected-company-id="selectedCompanyId"
                @company-changed="handleCompanyChanged"
                @filters-changed="handleFiltersChanged"
                @toggle-filters="showFilters = !showFilters"
                @clear-filters="clearFilters"
            />
        </b-card>

        <!-- Tabla de Diagnósticos -->
        <b-card v-if="selectedCompanyId">
            <b-table
                :items="diagnostics.data || []"
                :fields="fields"
                :busy="loading"
                striped
                hover
                responsive
                show-empty
                empty-text="No hay diagnósticos disponibles"
            >
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong class="ml-2">Cargando...</strong>
                    </div>
                </template>

                <template #cell(diagnostic_date)="data">
                    {{ formatDate(data.value) }}
                </template>

                <template #cell(follow_up_date)="data">
                    {{ formatDate(data.value) }}
                </template>

                <template #cell(is_active)="data">
                    <b-badge :variant="data.value ? 'success' : 'danger'">
                        {{ data.value ? 'Activo' : 'Inactivo' }}
                    </b-badge>
                </template>

                <template #cell(actions)="data">
                    <b-button 
                        size="sm" 
                        variant="info" 
                        @click="viewDiagnostic(data.item.id)"
                        class="mr-1"
                        v-b-tooltip.hover 
                        title="Ver detalle">
                        <i class="fa fa-eye"></i>
                    </b-button>
                    <b-button 
                        size="sm" 
                        variant="primary" 
                        @click="openDocumentsModal(data.item.id)"
                        class="mr-1"
                        v-b-tooltip.hover 
                        title="Ver documentos">
                        <i class="fa fa-file-text"></i>
                    </b-button>
                    <b-button 
                        size="sm" 
                        variant="warning" 
                        @click="editDiagnostic(data.item.id)"
                        class="mr-1"
                        v-b-tooltip.hover 
                        title="Editar">
                        <i class="fa fa-edit"></i>
                    </b-button>
                    <b-button 
                        size="sm" 
                        :variant="data.item.is_active ? 'danger' : 'success'" 
                        @click="toggleStatus(data.item)"
                        v-b-tooltip.hover 
                        :title="data.item.is_active ? 'Desactivar' : 'Activar'">
                        <i :class="data.item.is_active ? 'fa fa-ban' : 'fa fa-check'"></i>
                    </b-button>
                </template>
            </b-table>

            <!-- Paginación -->
            <b-row v-if="diagnostics.total > 0" class="mt-3">
                <b-col cols="12" md="6">
                    <p class="text-muted">
                        Mostrando {{ diagnostics.from }} a {{ diagnostics.to }} de {{ diagnostics.total }} registros
                    </p>
                </b-col>
                <b-col cols="12" md="6">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="diagnostics.total"
                        :per-page="perPage"
                        align="right"
                        @change="handlePageChange"
                    ></b-pagination>
                </b-col>
            </b-row>
        </b-card>

        <!-- Mensaje cuando no hay empresa seleccionada -->
        <b-card v-else class="text-center py-5">
            <i class="fa fa-building fa-3x text-muted mb-3"></i>
            <h4 class="text-muted">Seleccione una empresa para ver los diagnósticos</h4>
        </b-card>

        <!-- Modal de Formulario -->
        <form-diagnostic
            modal-id="modal-diagnostic"
            :company-id="selectedCompanyId"
            :diagnostic-id="editingDiagnosticId"
            @saved="handleDiagnosticSaved"
            @hidden="editingDiagnosticId = null"
        />

        <!-- Modal de Documentos -->
        <diagnostic-documents-modal
            :visible="showDocumentsModal"
            :diagnostic-id="selectedDiagnosticId"
            :company-id="selectedCompanyId"
            @close="showDocumentsModal = false"
            @document-uploaded="loadDiagnostics"
        />
    </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import FiltersDiagnostics from "./components/FiltersDiagnostics.vue";
import FormDiagnostic from "./components/FormDiagnostic.vue";
import DiagnosticDocumentsModal from "./components/DiagnosticDocumentsModal.vue";
import moment from "moment";

export default {
    name: "Diagnostics",
    components: {
        FiltersDiagnostics,
        FormDiagnostic,
        DiagnosticDocumentsModal,
    },
    data() {
        return {
            selectedCompanyId: null,
            loading: false,
            showFilters: false,
            editingDiagnosticId: null,
            showDocumentsModal: false,
            selectedDiagnosticId: null,
            filters: {},
            currentPage: 1,
            perPage: 10,
            fields: [
                { key: "check_list_company.check_list.name", label: "Lista de Chequeo", sortable: true },
                { key: "location.description", label: "Ubicación", sortable: true },
                { key: "responsible.names", label: "Champion / Responsable principal", sortable: true },
                { key: "diagnostic_date", label: "Fecha Diagnóstico", sortable: true },
                { key: "follow_up_date", label: "Fecha Seguimiento", sortable: true },
                { key: "is_active", label: "Estado", sortable: true },
                { key: "actions", label: "Acciones", thStyle: { width: "200px" }, tdClass: "text-nowrap" },
            ],
        };
    },
    computed: {
        ...mapState("skudo/diagnostics", ["diagnostics"]),
        ...mapState("tareas", ["empresas"]),
    },
    mounted() {
        // Si viene company_id en la query, cargar diagnósticos
        const companyIdFromQuery = this.$route.query.company_id;
        if (companyIdFromQuery) {
            this.selectedCompanyId = parseInt(companyIdFromQuery);
            this.loadDiagnostics();
        }
    },
    methods: {
        ...mapActions("skudo/diagnostics", ["getDiagnostics", "updateDiagnostic", "getDiagnosticById"]),

        handleCompanyChanged(companyId) {
            this.selectedCompanyId = companyId;
            if (companyId) {
                this.currentPage = 1;
                this.loadDiagnostics();
            } else {
                // Limpiar datos cuando no hay empresa
                this.$store.commit('skudo/diagnostics/setDiagnostics', {
                    data: [],
                    total: 0,
                    from: 0,
                    to: 0,
                    current_page: 1
                });
                this.filters = {};
            }
        },

        async loadDiagnostics() {
            if (!this.selectedCompanyId) return;

            this.loading = true;
            try {
                const params = {
                    company_id: this.selectedCompanyId,
                    page: this.currentPage,
                    per_page: this.perPage,
                    ...this.filters,
                };

                // Limpiar parámetros nulos
                Object.keys(params).forEach(key => {
                    if (params[key] === null || params[key] === "") {
                        delete params[key];
                    }
                });

                await this.getDiagnostics(params);
            } catch (error) {
                this.$alertError("Error al cargar los diagnósticos");
            } finally {
                this.loading = false;
            }
        },

        handleFiltersChanged(newFilters) {
            this.filters = newFilters;
            this.currentPage = 1;
            this.loadDiagnostics();
        },

        clearFilters() {
            // Resetear filtros en el componente hijo
            if (this.$refs.filtersComponent) {
                this.$refs.filtersComponent.filter = {
                    search: "",
                    location_id: null,
                    check_lists_companies_id: null,
                    responsible_id: null,
                    diagnostic_date: null,
                    is_active: null,
                };
                this.$refs.filtersComponent.diagnosticDateFrom = null;
                this.$refs.filtersComponent.diagnosticDateTo = null;
            }
            this.filters = {};
            this.currentPage = 1;
            this.loadDiagnostics();
        },

        handlePageChange(page) {
            this.currentPage = page;
            this.loadDiagnostics();
        },

        formatDate(date) {
            return date ? moment(date).format("DD/MM/YYYY") : "-";
        },

        viewDiagnostic(id) {
            this.$router.push({ 
                name: "DiagnosticDetail", 
                params: { id },
                query: { company_id: this.selectedCompanyId }
            });
        },

        openDocumentsModal(diagnosticId) {
            this.selectedDiagnosticId = diagnosticId;
            this.showDocumentsModal = true;
        },

        openCreateModal() {
            this.editingDiagnosticId = null;
            this.$bvModal.show('modal-diagnostic');
        },

        editDiagnostic(id) {
            this.editingDiagnosticId = id;
            this.$nextTick(() => {
                this.$bvModal.show('modal-diagnostic');
            });
        },

        handleDiagnosticSaved() {
            this.loadDiagnostics();
        },

        async toggleStatus(item) {
            try {
                const newStatus = item.is_active ? 0 : 1;
                const action = newStatus ? 'activar' : 'inactivar';
                
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `¿Está seguro de ${action} este diagnóstico?`, 
                    {
                        title: 'Confirmar acción',
                        size: 'md',
                        buttonSize: 'md',
                        okVariant: 'primary',
                        okTitle: 'Confirmar',
                        cancelVariant: 'danger',
                        cancelTitle: 'Cancelar',
                        footerClass: 'p-2',
                        hideHeaderClose: false,
                        centered: true
                    }
                );
                
                if (!confirmed) return;
                
                // Cargar el diagnóstico completo
                const response = await this.getDiagnosticById({
                    id: item.id,
                    company_id: this.selectedCompanyId
                });
                const diagnostic = response.data.data || response.data;
                
                // Actualizar solo el campo is_active pero enviar todo el registro
                const payload = {
                    company_id: diagnostic.company_id || this.selectedCompanyId,
                    check_lists_companies_id: diagnostic.check_lists_companies_id,
                    location_id: diagnostic.location_id,
                    responsible_id: diagnostic.responsible_id,
                    diagnostic_date: diagnostic.diagnostic_date,
                    follow_up_date: diagnostic.follow_up_date,
                    observations: diagnostic.observations,
                    is_active: newStatus
                };
                
                await this.updateDiagnostic({
                    id: item.id,
                    data: payload
                });
                
                this.$alertSuccess(`Diagnóstico ${action === 'activar' ? 'activado' : 'inactivado'} exitosamente`);
                this.loadDiagnostics();
            } catch (error) {
                this.$alertError('Error al cambiar el estado del diagnóstico');
            }
        },
    },
};
</script>